import { Dispatch, FC, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { GridSortItem } from '@mui/x-data-grid';
import { ProjectStatusEnum } from '~/api/types';
import { useCreateWorkbenchMutation, useGetProjectQuery } from '~/api/v2/hooks';
import { ButtonSpinner, Tooltip } from '~/components';
import { TableFilters } from '~/components/Table/components/filters/types';
import { ProjectActionTypeEnum, useGetWorkbenchesQuery, useProjectActionState } from '~/hooks';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUserApplicationRole, selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { getPriviligies } from '~/utils';
import { actionsContainerStyles, headerContainerStyles } from './styles';

interface WorkbenchTabHeaderProps {
  setShowTerminated: Dispatch<SetStateAction<boolean>>;
  showTerminated: boolean;
  searchQuery: string;
  filters: TableFilters;
  sortModel: GridSortItem | null;
}

const WorkbenchTabHeader: FC<WorkbenchTabHeaderProps> = ({
  setShowTerminated,
  showTerminated,
  searchQuery,
  filters,
  sortModel,
}) => {
  const projectId = +useParams().projectid!;
  const userId = useAppSelector(selectCurrentUserId);
  const dispatch = useAppDispatch();
  const userApplicationRole = useAppSelector(selectCurrentUserApplicationRole);
  const { data } = useGetProjectQuery({ projectId });
  const { isFetching, refetch } = useGetWorkbenchesQuery({
    projectId,
    showTerminated,
    searchQuery,
    sortModel,
    filters,
  });

  const [, { isLoading: isCreatingWorkbench }] = useCreateWorkbenchMutation({
    fixedCacheKey: `create-workbench-project-${projectId}`,
  });
  const userProjectRole = data?.members?.find(({ id }) => id === userId)?.role_id;
  const hasAccessToOperate =
    getPriviligies(userProjectRole)?.workbenchTab.canCreate ||
    getPriviligies(userApplicationRole)?.workbenchTab.canCreate;
  const { isDisabledAction, tooltipMessage } = useProjectActionState({
    actionType: ProjectActionTypeEnum.WORKBENCH,
  });
  const isWorkbenchCreationDisabled = isCreatingWorkbench || isDisabledAction;

  const handleShowTerminatedToggle = () => {
    setShowTerminated(!showTerminated);
    refetch();
  };

  return (
    <Box sx={headerContainerStyles}>
      <Typography variant="h3">Workbench Management</Typography>
      <Box sx={actionsContainerStyles}>
        <FormControlLabel
          labelPlacement="end"
          label="Show terminated workbenches"
          control={<Switch checked={showTerminated} onChange={handleShowTerminatedToggle} sx={{ mr: '1rem' }} />}
        />
        <CustomButton
          buttonType="outlined"
          disabled={isFetching || isCreatingWorkbench || data?.status === ProjectStatusEnum.INACTIVE}
          onClick={() => refetch()}
          style={{ width: 170, textTransform: 'none' }}
        >
          <Box display="flex">
            {isFetching ? 'Updating' : 'Refresh'}
            {isFetching && <ButtonSpinner />}
          </Box>
        </CustomButton>
        {hasAccessToOperate && (
          <Tooltip disableHoverListener={!isWorkbenchCreationDisabled} title={tooltipMessage}>
            <span>
              <CustomButton
                onClick={() => {
                  dispatch(openModal({ type: ModalTypesEnum.CREATE_WORKBENCH }));
                }}
                disabled={isWorkbenchCreationDisabled}
                style={{ width: 250, textTransform: 'none' }}
              >
                <Box display="flex">
                  Create Workbench
                  {isCreatingWorkbench && <ButtonSpinner />}
                </Box>
              </CustomButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default WorkbenchTabHeader;
